import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { MatDialog } from "@angular/material/dialog";
import { MatAutocomplete } from "@angular/material/autocomplete";

import { TranslateService } from "@ngx-translate/core";

import { Observable, Subscription } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loadedGradeIsLoadingState,
  loadedGradeListState,
  siteGradeListLoadedState,
} from "src/app/store/reducers";
import {
  createGradeList,
  getSiteGradeList,
} from "src/app/store/actions/sites/sites.actions";
import { getGradesList } from "src/app/store/actions/grades/grades.actions";

import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";
import { GradeDTO } from "src/app/models/Grades/GradeDTO";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { FormUtils } from "src/app/shared/utils/formUtils";

import { BaseSiteProducts } from "src/app/shared/base-classes/base-site-products/base-site-products";
import { CanComponentDeactivate } from "src/app/shared";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { Router } from "@angular/router";

// Grade Detail Form
const GRADE_FORM_PARAMS = {
  gradeList: "gradeList",
  siteCode: "siteCode",
  grade: "grade",
  fccGradeCode: "fccGradeCode",
};

@Component({
  selector: "site-grades",
  templateUrl: "./grades.component.html",
  styleUrls: ["./grades.component.scss"],
})
export class GradesComponent
  extends BaseSiteProducts
  implements OnDestroy, CanComponentDeactivate
{
  @Input() set siteCode(code: string) {
    if (code) {
      this._siteCode = code;
      this.createGradeForm();
      this.loadSiteGradeList(this._siteCode);
      this.setGradeSearchSub();
    }
  }
  @Input() duplication: boolean;

  @Output() getGradesForm: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();

  _siteCode: string;

  // Site Grade List
  siteGradeList$: Observable<SiteGradeDTO[]>;
  siteGradeList: SiteGradeDTO[];
  siteGradeListSub: Subscription;

  // Grade Search
  isLoadingGrades$: Observable<boolean>;
  gradeList$: Observable<GradeDTO[]>;
  gradeList: GradeDTO[] = [];
  gradeListSub: Subscription;
  searchTimer: number;

  gradeDataForm: FormGroup;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    public dialog: MatDialog
  ) {
    super(dialog);

    this.siteGradeList$ = this.store.pipe(select(siteGradeListLoadedState));
    this.isLoadingGrades$ = this.store.pipe(select(loadedGradeIsLoadingState));
    this.gradeList$ = this.store.pipe(select(loadedGradeListState));
  }

  loadSiteGradeList(code: string) {
    if (this.duplication) {
      return;
    }
    this.siteGradeListSub = this.siteGradeList$.subscribe((gradeList) => {
      if (gradeList) {
        this.populateGrades(gradeList);
      }
    });
    this.store.dispatch(getSiteGradeList({ code }));
  }

  createGradeForm() {
    this.gradeDataForm = this.fb.group({
      [GRADE_FORM_PARAMS.gradeList]: this.fb.array([]),
    });
  }

  getGradesFormPristine() {
    this.getGradesForm.emit(this.gradeDataForm);
  }

  populateGrades(gradeList: SiteGradeDTO[]) {
    const grades = this.getFormArray(
      this.gradeDataForm,
      GRADE_FORM_PARAMS.gradeList
    );
    this.clearFormArray(grades);
    gradeList.forEach((grade) => {
      const gradeFormGroup = this.createGradeFormGroup(grade);
      grades.push(gradeFormGroup);
    });
  }

  createGradeFormGroup(grade?: SiteGradeDTO): FormGroup {
    const { siteCode, gradeCode, gradeDescription, fccGradeCode } = grade || {};
    return this.fb.group({
      [GRADE_FORM_PARAMS.siteCode]: [siteCode ?? this._siteCode],
      [GRADE_FORM_PARAMS.grade]: [
        gradeCode ? { code: gradeCode, description: gradeDescription } : null,
        [Validators.required, FormUtils.isObjectValidator],
      ],
      [GRADE_FORM_PARAMS.fccGradeCode]: [
        fccGradeCode ?? null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
    });
  }

  addGrade() {
    const grades = this.getFormArray(
      this.gradeDataForm,
      GRADE_FORM_PARAMS.gradeList
    );
    const gradeFormGroup = this.createGradeFormGroup();
    grades.push(gradeFormGroup);
    this.gradeDataForm.markAsDirty();
  }

  openRemoveDialog(index: number) {
    this.openDefaultDialog(index, this.removeGrade.bind(this));
  }

  removeGrade(index: number) {
    const grades = this.getFormArray(
      this.gradeDataForm,
      GRADE_FORM_PARAMS.gradeList
    );
    grades.removeAt(index);
    this.gradeDataForm.markAsDirty();
    this.getGradesFormPristine();
  }

  setGradeSearchSub() {
    this.gradeList = [];
    this.gradeListSub = this.gradeList$.pipe(skip(1)).subscribe((grades) => {
      if (grades) {
        this.gradeList = grades.map(({ code, description }) => {
          return {
            code: code,
            description: description ?? "",
          };
        });
      }
    });
  }

  validateGradeSearch(
    event: KeyboardEvent,
    gradeAutocomplete: MatAutocomplete
  ) {
    if (gradeAutocomplete.isOpen) {
      return;
    }
    this.searchGrades(event, false);
  }

  searchGrades(event: KeyboardEvent, hasTimer = true) {
    const { value: searchTerm } = event.target as HTMLInputElement;

    clearTimeout(this.searchTimer);

    this.searchTimer = window.setTimeout(
      () => {
        this.gradeList = [];
        this.store.dispatch(
          getGradesList({
            options: {
              filteringArgs: {
                filterConfig: [{ field: "code" }, { field: "description" }],
                searchTerms: [searchTerm],
              },
            },
          })
        );
      },
      hasTimer ? 500 : 0
    );
  }

  displayGrade(grade: { code: string; description: string }): string {
    return grade?.description ?? "";
  }

  saveSiteGradeList() {
    if (this.gradeDataForm.pristine) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (!this.gradeDataForm.valid) {
      this.gradeDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.SiteGradeFormInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    const siteGradeList: SiteGradeDTO[] = this.gradeDataForm
      .get(GRADE_FORM_PARAMS.gradeList)
      .value.map((siteGrade) => {
        return {
          siteCode: siteGrade.siteCode,
          gradeCode: siteGrade.grade.code,
          fccGradeCode: siteGrade.fccGradeCode.toString(),
        };
      });

    if (this.repeatedGrades(siteGradeList)) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.RepeatRuleGrade"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (siteGradeList) {
      this.store.dispatch(
        createGradeList({
          siteCode: this._siteCode,
          gradeList: siteGradeList,
        })
      );
      this.gradeDataForm.markAsUntouched();
      this.gradeDataForm.markAsPristine();
      this.getGradesFormPristine();
    }
  }

  repeatedGrades(siteGradeList: SiteGradeDTO[]): boolean {
    return siteGradeList.some((gradeEl) => {
      const filteredGrades = siteGradeList.filter(
        (grade) =>
          grade.gradeCode === gradeEl.gradeCode ||
          grade.fccGradeCode === gradeEl.fccGradeCode
      );
      return filteredGrades.length > 1;
    });
  }

  ngOnDestroy() {
    if (this.siteGradeListSub) {
      this.siteGradeListSub.unsubscribe();
    }
    if (this.gradeListSub) {
      this.gradeListSub.unsubscribe();
    }
  }
  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.gradeDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
